// frontend/src/pages/looks/CollectionsPage.js
// W3 — Saved Looks / Collections manager (account required).
// Lists the user's collections with look counts, lets them create / delete
// collections, and view the looks saved in each collection.

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaBookmark, FaRegBookmark, FaTrash, FaPlus } from "react-icons/fa";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";

const DEFAULT_API = process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

function CollectionsPage() {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newPublic, setNewPublic] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeCollection, setActiveCollection] = useState(null); // { collection, looks[] }
  const [activeLoading, setActiveLoading] = useState(false);

  const getToken = () => localStorage.getItem("ogestyle_token");
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768,
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Redirect unauthenticated visitors to login.
  useEffect(() => {
    const token = getToken();
    if (!token) {
      const path = `${window.location.pathname}${window.location.search}`;
      navigate(`/login?redirect=${encodeURIComponent(path)}`);
    }
  }, [navigate]);

  const loadCollections = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${DEFAULT_API}/api/looks-social/collections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Unable to load collections");
      setCollections(data.collections || []);
    } catch (err) {
      setError(err.message || "Unable to load collections");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  const createCollection = async () => {
    const token = getToken();
    if (!token) return;
    const name = newName.trim();
    if (!name) {
      setError("Collection name is required");
      return;
    }
    setCreating(true);
    setError("");
    try {
      const res = await fetch(`${DEFAULT_API}/api/looks-social/collections`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, description: newDesc.trim(), isPublic: newPublic }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Unable to create collection");
      setCollections((prev) => [data.collection, ...prev]);
      setShowCreate(false);
      setNewName("");
      setNewDesc("");
      setNewPublic(false);
    } catch (err) {
      setError(err.message || "Unable to create collection");
    } finally {
      setCreating(false);
    }
  };

  const deleteCollection = async (collectionId) => {
    if (!window.confirm("Delete this collection and remove all saved looks in it?")) return;
    const token = getToken();
    if (!token) return;
    setError("");
    try {
      const res = await fetch(`${DEFAULT_API}/api/looks-social/collections/${collectionId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data?.message || "Unable to delete collection");
      }
      setCollections((prev) => prev.filter((c) => String(c._id) !== String(collectionId)));
      setActiveCollection(null);
    } catch (err) {
      setError(err.message || "Unable to delete collection");
    }
  };

  const openCollection = async (collectionId) => {
    const token = getToken();
    if (!token) return;
    setActiveLoading(true);
    setError("");
    setActiveCollection({ collection: collections.find((c) => String(c._id) === String(collectionId)), looks: [] });
    try {
      const res = await fetch(`${DEFAULT_API}/api/looks-social/collections/${collectionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Unable to load collection");
      setActiveCollection({ collection: data.collection, looks: data.looks || [] });
    } catch (err) {
      setError(err.message || "Unable to load collection");
      setActiveCollection(null);
    } finally {
      setActiveLoading(false);
    }
  };

  const unsaveFromActive = async (lookId) => {
    const token = getToken();
    const collectionId = activeCollection?.collection?._id;
    if (!token || !collectionId) return;
    try {
      await fetch(`${DEFAULT_API}/api/looks-social/collections/${collectionId}/save/${lookId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveCollection((prev) => ({
        ...prev,
        looks: (prev?.looks || []).filter((l) => String(l._id) !== String(lookId)),
        collection: { ...(prev?.collection || {}), lookCount: Math.max(0, (prev?.collection?.lookCount || 1) - 1) },
      }));
      setCollections((prev) =>
        prev.map((c) =>
          String(c._id) === String(collectionId)
            ? { ...c, lookCount: Math.max(0, (c.lookCount || 1) - 1) }
            : c,
        ),
      );
    } catch (err) {
      setError("Unable to remove look");
    }
  };

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      <CustomerHeader />
      <main
        style={{
          maxWidth: "1080px",
          margin: "0 auto",
          padding: isMobile
            ? "calc(var(--demo-banner-height, 0px) + 76px) 16px 48px"
            : "calc(var(--demo-banner-height, 0px) + 90px) 24px 64px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px", marginBottom: "18px" }}>
          <div>
            <Link to="/looks/all" style={{ color: "#2563eb", fontSize: "13px", fontWeight: 600, textDecoration: "none" }}>
              ← All Looks
            </Link>
            <h1 style={{ margin: "8px 0 2px", fontSize: isMobile ? "20px" : "26px", fontWeight: 700, color: "#0f172a" }}>
              <FaBookmark style={{ verticalAlign: "middle", marginRight: "8px", color: "#1d4f80" }} />
              Saved Looks
            </h1>
            <p style={{ margin: 0, color: "#6b7280", fontSize: "13px" }}>
              Your collections of looks to revisit or book later.
            </p>
          </div>
          <button
            onClick={() => setShowCreate((s) => !s)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "7px",
              border: "none",
              borderRadius: "999px",
              background: "#1d4f80",
              color: "#fff",
              padding: "10px 18px",
              fontSize: "13px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            <FaPlus size={12} /> New Collection
          </button>
        </div>

        {error && (
          <div style={{ marginBottom: "12px", padding: "10px 12px", border: "1px solid #fecaca", borderRadius: "8px", background: "#fef2f2", color: "#991b1b", fontSize: "13px" }}>
            {error}
          </div>
        )}

        {showCreate && (
          <div style={{ border: "1px solid #e5e7eb", borderRadius: "12px", padding: "16px", marginBottom: "18px", background: "#fafafa" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Collection name (e.g. Balayage inspo)"
                maxLength={60}
                style={{ border: "1px solid #d1d5db", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
              />
              <input
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Description (optional)"
                maxLength={300}
                style={{ border: "1px solid #d1d5db", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" }}
              />
              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#374151" }}>
                <input type="checkbox" checked={newPublic} onChange={(e) => setNewPublic(e.target.checked)} />
                Make this collection public
              </label>
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={createCollection}
                  disabled={creating}
                  style={{ border: "none", borderRadius: "8px", background: "#1d4f80", color: "#fff", padding: "9px 16px", fontSize: "13px", fontWeight: 700, cursor: creating ? "default" : "pointer" }}
                >
                  {creating ? "Creating…" : "Create Collection"}
                </button>
                <button
                  onClick={() => setShowCreate(false)}
                  style={{ border: "1px solid #d1d5db", borderRadius: "8px", background: "#fff", color: "#374151", padding: "9px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {activeCollection ? (
          <div style={{ marginBottom: "18px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px", marginBottom: "12px" }}>
              <button
                onClick={() => setActiveCollection(null)}
                style={{ border: "none", background: "transparent", color: "#2563eb", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}
              >
                ← All collections
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "13px", color: "#6b7280" }}>
                  {activeCollection.collection?.lookCount || 0} saved
                </span>
                <button
                  onClick={() => deleteCollection(activeCollection.collection._id)}
                  style={{ display: "inline-flex", alignItems: "center", gap: "5px", border: "1px solid #fecaca", borderRadius: "8px", background: "#fff", color: "#9f1239", padding: "6px 12px", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}
                >
                  <FaTrash size={11} /> Delete
                </button>
              </div>
            </div>

            <h2 style={{ margin: "0 0 4px", fontSize: "18px", fontWeight: 700, color: "#0f172a" }}>
              {activeCollection.collection?.name}
            </h2>
            {activeCollection.collection?.description && (
              <p style={{ margin: "0 0 12px", color: "#6b7280", fontSize: "13px" }}>{activeCollection.collection.description}</p>
            )}

            {activeLoading ? (
              <div style={{ padding: "30px 0", textAlign: "center", color: "#9ca3af" }}>Loading…</div>
            ) : activeCollection.looks.length === 0 ? (
              <div style={{ padding: "30px 0", textAlign: "center", color: "#6b7280", border: "1px dashed #d1d5db", borderRadius: "12px" }}>
                Nothing saved here yet. Tap "Save" on any look.
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "repeat(3, minmax(0,1fr))" : "repeat(4, minmax(0,1fr))",
                  gap: "10px",
                }}
              >
                {activeCollection.looks.map((look) => (
                  <div key={look._id} style={{ position: "relative" }}>
                    <div
                      onClick={() => navigate(`/look/${look._id}`)}
                      style={{ cursor: "pointer", background: "#111", lineHeight: 0, borderRadius: "10px", overflow: "hidden" }}
                    >
                      {look.imageUrl ? (
                        <img src={look.imageUrl} alt={look.serviceName} style={{ width: "100%", display: "block" }} />
                      ) : (
                        <div style={{ height: "140px", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "12px" }}>
                          No image
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "#374151", margin: "4px 2px 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {look.serviceName}
                    </div>
                    <button
                      onClick={() => unsaveFromActive(look._id)}
                      title="Remove from collection"
                      aria-label="Remove from collection"
                      style={{
                        position: "absolute",
                        top: "6px",
                        right: "6px",
                        width: "26px",
                        height: "26px",
                        borderRadius: "999px",
                        border: "none",
                        background: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                    >
                      <FaTrash size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : loading ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: "#9ca3af" }}>Loading…</div>
        ) : collections.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: "#6b7280", border: "1px dashed #d1d5db", borderRadius: "12px" }}>
            <FaRegBookmark size={28} style={{ color: "#9ca3af", marginBottom: "8px" }} />
            <div>You haven't saved any looks yet.</div>
            <Link to="/looks/all" style={{ color: "#2563eb", fontSize: "13px", fontWeight: 600 }}>
              Browse all looks →
            </Link>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(2, minmax(0,1fr))" : "repeat(3, minmax(0,1fr))",
              gap: "12px",
            }}
          >
            {collections.map((col) => (
              <div
                key={col._id}
                onClick={() => openCollection(col._id)}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "14px",
                  cursor: "pointer",
                  background: "#fff",
                  boxShadow: "0 2px 6px rgba(15,23,42,0.04)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <FaBookmark style={{ color: "#1d4f80", flexShrink: 0 }} size={16} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {col.name}
                    </div>
                    <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                      {col.lookCount} look{col.lookCount === 1 ? "" : "s"}
                      {col.isPublic ? " · public" : " · private"}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <CustomerFooter />
    </div>
  );
}

export default CollectionsPage;
